import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Create axios instance with credentials for HttpOnly cookie support
const api = axios.create({
  baseURL: API_URL,
  withCredentials: true,  // Enable sending cookies with cross-origin requests
  timeout: 120000,  // 2 minute default timeout
  maxContentLength: Infinity,
  maxBodyLength: Infinity
});

// Add token from localStorage as fallback (for backward compatibility)
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('session_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle 401 responses by clearing auth state
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('session_token');
    }
    return Promise.reject(error);
  }
);

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const checkAuth = useCallback(async () => {
    try {
      // Authenticate using HttpOnly cookie (with localStorage token as fallback)
      const response = await api.get('/api/auth/me');
      setUser(response.data);
      return response.data;
    } catch (err) {
      // Clear localStorage token if auth fails
      localStorage.removeItem('session_token');
      setUser(null);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  // Refresh user data (useful after purchases, etc.)
  const refreshUser = useCallback(async () => {
    try {
      const response = await api.get('/api/auth/me');
      setUser(response.data);
      return response.data;
    } catch (err) {
      console.error('Failed to refresh user:', err);
      return null;
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password, isImpersonation = false, impersonationData = null) => {
    try {
      setError(null);
      
      // Handle admin impersonation
      if (isImpersonation && impersonationData) {
        if (impersonationData.session_token) {
          localStorage.setItem('session_token', impersonationData.session_token);
        }
        setUser(impersonationData.user);
        return impersonationData;
      }
      
      const response = await api.post('/api/auth/login', {
        email,
        password
      });
      
      // Store token in localStorage as backup (backend sets HttpOnly cookie)
      if (response.data.access_token) {
        localStorage.setItem('session_token', response.data.access_token);
      }
      
      setUser(response.data.user);
      return response.data;
    } catch (err) {
      const message = err.response?.data?.detail || 'Login failed';
      setError(message);
      throw new Error(message);
    }
  };

  const register = async (name, email, password, trackingData = {}) => {
    try {
      setError(null);
      const response = await api.post('/api/auth/register', {
        name,
        email,
        password,
        utm_source: trackingData.utm_source || null,
        utm_medium: trackingData.utm_medium || null,
        utm_content: trackingData.utm_content || null,
        utm_campaign: trackingData.utm_campaign || null,
        referral_code: trackingData.referral_code || null
      });
      
      // Store token in localStorage as backup (backend sets HttpOnly cookie)
      if (response.data.access_token) {
        localStorage.setItem('session_token', response.data.access_token);
      }
      
      setUser(response.data.user);
      return response.data;
    } catch (err) {
      const message = err.response?.data?.detail || 'Registration failed';
      setError(message);
      throw new Error(message);
    }
  };

  const loginWithGoogle = async () => {
    // Capture UTM parameters from current URL to pass through Google OAuth flow
    const urlParams = new URLSearchParams(window.location.search);
    const utmData = {
      utm_source: urlParams.get('utm_source') || '',
      utm_medium: urlParams.get('utm_medium') || '',
      utm_campaign: urlParams.get('utm_campaign') || '',
      utm_content: urlParams.get('utm_content') || '',
      referral_code: urlParams.get('ref') || ''
    };
    
    // Store UTM data in sessionStorage to retrieve after OAuth redirect
    // Use try-catch for browsers with storage restrictions (private mode, etc.)
    try {
      sessionStorage.setItem('oauth_utm_data', JSON.stringify(utmData));
    } catch (e) {
      console.warn('Could not store UTM data:', e);
    }
    
    try {
      const { data } = await api.get('/api/auth/google/start');
      window.location.href = data.auth_url;
    } catch (err) {
      setError("La connexion Google est indisponible. Utilisez votre email et mot de passe.");
    }
  };

  const processGoogleCallback = async (sessionId) => {
    try {
      setError(null);
      console.log('[AuthContext] processGoogleCallback started');
      console.log('[AuthContext] Session ID:', sessionId?.substring(0, 20) + '...');
      console.log('[AuthContext] API_URL:', API_URL);
      
      // Retrieve UTM data stored before OAuth redirect
      const storedUtmData = sessionStorage.getItem('oauth_utm_data');
      const utmData = storedUtmData ? JSON.parse(storedUtmData) : {};
      sessionStorage.removeItem('oauth_utm_data'); // Clean up
      console.log('[AuthContext] UTM data:', utmData);
      
      // Use the api instance which has the correct baseURL and credentials
      console.log('[AuthContext] Calling API: /api/auth/session');
      const response = await api.post('/api/auth/session', utmData, {
        headers: { 'X-Session-ID': sessionId }
      });
      
      console.log('[AuthContext] API response status:', response.status);
      console.log('[AuthContext] User email:', response.data?.email);
      
      // Store token in localStorage as backup (backend sets HttpOnly cookie)
      if (response.data.session_token) {
        localStorage.setItem('session_token', response.data.session_token);
        console.log('[AuthContext] Session token stored');
      }
      
      setUser(response.data);
      console.log('[AuthContext] User set, returning data');
      return response.data;
    } catch (err) {
      console.error('[AuthContext] processGoogleCallback error:', err);
      console.error('[AuthContext] Error response:', err.response?.data);
      console.error('[AuthContext] Error status:', err.response?.status);
      const message = err.response?.data?.detail || 'Google authentication failed';
      setError(message);
      throw new Error(message);
    }
  };

  const logout = async () => {
    try {
      // Call logout endpoint to clear server-side session and cookie
      await api.post('/api/auth/logout');
    } catch (err) {
      console.error('Logout error:', err);
    } finally {
      // Always clear local state
      localStorage.removeItem('session_token');
      setUser(null);
    }
  };

  const value = {
    user,
    loading,
    error,
    login,
    register,
    loginWithGoogle,
    processGoogleCallback,
    logout,
    checkAuth,
    refreshUser,
    isAuthenticated: !!user
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

// Export api instance for use in other components
export { api };

export default AuthContext;
